import React, { useCallback, useEffect, useState } from "react";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { DashboardPageHeader } from "../../../shared/components/dashboard/DashboardBento";
import { useTranslation } from "../../../i18n";
import {
  adminReplyToTicket,
  adminSetTicketStatus,
  fetchAdminTickets,
} from "../../../client/dashboard/supportApi";
import {
  TicketCategoryBadge,
  TicketMessages,
  TicketStatusBadge,
  formatTicketDate,
} from "../../../shared/components/support/TicketThread";

const STATUS_FILTERS = ["", "open", "answered", "closed"];
const CATEGORY_FILTERS = ["", "support", "feedback"];

const AdminSupportTicketsPage = () => {
  const { t, locale } = useTranslation();
  const [tickets, setTickets] = useState([]);
  const [count, setCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [replyDrafts, setReplyDrafts] = useState({});
  const [busy, setBusy] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAdminTickets({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        search: search.trim() || undefined,
      });
      setTickets(data.results);
      setCount(data.count);
      setError("");
    } catch {
      setError(t("adminSupport.loadError"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, search, t]);

  useEffect(() => {
    const timer = window.setTimeout(loadTickets, search ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [loadTickets, search]);

  const patchTicket = (updated) => {
    setTickets((prev) => prev.map((tk) => (tk.id === updated.id ? updated : tk)));
  };

  const handleReply = async (ticketId) => {
    const draft = (replyDrafts[ticketId] || "").trim();
    if (!draft || busy) return;
    setBusy(true);
    try {
      const updated = await adminReplyToTicket(ticketId, draft);
      patchTicket(updated);
      setReplyDrafts((prev) => ({ ...prev, [ticketId]: "" }));
    } catch {
      setError(t("adminSupport.loadError"));
    } finally {
      setBusy(false);
    }
  };

  const handleStatus = async (ticketId, status) => {
    if (busy) return;
    setBusy(true);
    try {
      patchTicket(await adminSetTicketStatus(ticketId, status));
    } catch {
      setError(t("adminSupport.loadError"));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel = (value) =>
    value === ""
      ? t("adminSupport.filterAll")
      : value === "open"
        ? t("support.statusOpen")
        : value === "answered"
          ? t("support.statusAnswered")
          : t("support.statusClosed");

  const openCount = tickets.filter((tk) => tk.status === "open").length;

  return (
    <div className="bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto min-h-screen max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <DashboardPageHeader
          eyebrow={t("adminSupport.eyebrow")}
          title={t("adminSupport.title")}
          subtitle={t("adminSupport.subtitle")}
          actions={
            <span className="inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 font-label-sm font-medium text-on-surface-variant">
              <MaterialIcon name="support_agent" className="text-[16px] text-secondary" />
              {t("adminSupport.openCount", { count: openCount })}
            </span>
          }
        />

        <div className="mb-stack-md flex flex-wrap items-center gap-3">
          <div className="flex rounded-full border border-outline-variant/40 bg-surface-container-lowest p-1">
            {STATUS_FILTERS.map((value) => (
              <button
                key={value || "all"}
                type="button"
                onClick={() => setStatusFilter(value)}
                className={`rounded-full px-4 py-1.5 font-label-sm font-medium transition-colors ${
                  statusFilter === value
                    ? "bg-primary text-on-primary"
                    : "text-on-surface-variant hover:text-on-surface"
                }`}
              >
                {statusLabel(value)}
              </button>
            ))}
          </div>
          <div className="flex rounded-full border border-outline-variant/40 bg-surface-container-lowest p-1">
            {CATEGORY_FILTERS.map((value) => (
              <button
                key={value || "all-types"}
                type="button"
                onClick={() => setCategoryFilter(value)}
                className={`rounded-full px-4 py-1.5 font-label-sm font-medium transition-colors ${
                  categoryFilter === value
                    ? "bg-primary text-on-primary"
                    : "text-on-surface-variant hover:text-on-surface"
                }`}
              >
                {value === ""
                  ? t("adminSupport.filterAllTypes")
                  : value === "support"
                    ? t("support.categorySupportShort")
                    : t("support.categoryFeedbackShort")}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("adminSupport.searchPlaceholder")}
            className="w-full max-w-xs rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2.5 outline-none focus:ring-2 focus:ring-secondary"
          />
        </div>

        {error ? (
          <p className="mb-4 rounded-lg bg-error-container px-4 py-3 text-label-sm text-on-error-container">
            {error}
          </p>
        ) : null}

        {loading && tickets.length === 0 ? null : tickets.length === 0 ? (
          <p className="font-body-md text-on-surface-variant">{t("adminSupport.noTickets")}</p>
        ) : (
          <ul className="space-y-3">
            {tickets.map((ticket) => {
              const isExpanded = expandedId === ticket.id;
              return (
                <li
                  key={ticket.id}
                  className="tonal-card overflow-hidden rounded-xl border border-outline-variant/30"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : ticket.id)}
                    className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
                  >
                    <span className="flex min-w-0 items-center gap-3">
                      <MaterialIcon
                        name={isExpanded ? "expand_more" : "chevron_right"}
                        className="shrink-0 text-[18px] text-on-surface-variant"
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-primary">
                          {ticket.subject}
                        </span>
                        {ticket.submitter ? (
                          <span className="mt-0.5 block truncate font-label-sm text-on-surface-variant">
                            {t("adminSupport.submittedBy")}: {ticket.submitter.fullName || "—"} ·{" "}
                            {ticket.submitter.email}
                            {ticket.submitter.companyName
                              ? ` · ${ticket.submitter.companyName}`
                              : ""}
                          </span>
                        ) : null}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="font-label-sm text-outline">
                        {formatTicketDate(ticket.createdAt, locale)}
                      </span>
                      <TicketCategoryBadge category={ticket.category} t={t} />
                      <TicketStatusBadge status={ticket.status} t={t} />
                    </span>
                  </button>

                  {isExpanded ? (
                    <div className="border-t border-outline-variant/30 bg-surface-container-low/40 p-4">
                      <TicketMessages
                        messages={ticket.messages}
                        t={t}
                        locale={locale}
                        perspective="admin"
                      />

                      <div className="mt-4 flex items-end gap-2">
                        <textarea
                          value={replyDrafts[ticket.id] || ""}
                          onChange={(event) =>
                            setReplyDrafts((prev) => ({
                              ...prev,
                              [ticket.id]: event.target.value,
                            }))
                          }
                          rows={2}
                          className="w-full resize-y rounded-xl border border-outline-variant bg-surface-container-lowest px-4 py-2.5 outline-none focus:ring-2 focus:ring-secondary"
                          placeholder={t("adminSupport.replyPlaceholder")}
                        />
                        <button
                          type="button"
                          disabled={busy || !(replyDrafts[ticket.id] || "").trim()}
                          onClick={() => handleReply(ticket.id)}
                          className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-label-sm font-medium text-on-primary disabled:opacity-50"
                        >
                          {t("adminSupport.replySend")}
                          <MaterialIcon name="send" className="text-[16px]" />
                        </button>
                      </div>

                      <div className="mt-3 flex justify-end">
                        {ticket.status === "closed" ? (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleStatus(ticket.id, "open")}
                            className="inline-flex items-center gap-1.5 font-label-sm font-semibold text-secondary hover:underline disabled:opacity-50"
                          >
                            <MaterialIcon name="refresh" className="text-[16px]" />
                            {t("adminSupport.reopen")}
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => handleStatus(ticket.id, "closed")}
                            className="inline-flex items-center gap-1.5 font-label-sm font-semibold text-on-surface-variant hover:text-error disabled:opacity-50"
                          >
                            <MaterialIcon name="cancel" className="text-[16px]" />
                            {t("adminSupport.close")}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {count > tickets.length ? (
          <p className="mt-4 font-label-sm text-on-surface-variant">
            {tickets.length} / {count}
          </p>
        ) : null}
      </div>
    </div>
  );
};

export default AdminSupportTicketsPage;
